import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import Error from '$components/Error';
import PageContainer from '$components/PageContainer';
import Assignments from '$components/Assignments';
import Spinner from '$components/Spinner';
import AssessmentItemsView from '$components/AssessmentItemsView';
import AutoLaunchExternalLink from '$components/ExternalLink/AutoLaunchExternalLink';
import StudentDetails from '$components/StudentDetails';
import ContextBar from '$components/ContextBar';
import { EDUCATOR, STUDENT } from '$variables/constants';
import * as TYPES from '$variables/types';

import {
  Assignment as AssignmentType,
  ErrorType,
  AssessmentItem as AssessmentItemType,
  User,
} from '$variables/types';

import { StudentDetailsWrapper } from './StudentPage.styled';
import { MediumUp } from '$components/Responsive';

const StudentPage = ({
  studentId,
  spaceId,
  currentSpace,
  viewAs,
  error,
  isLoading,
  student,
  assessmentItems,
  fetchAssessmentItems,
  assignments,
  fetchLtiProviders,
  ltiProviders,
  fetchAssignments,
  fetchGradesForStudent,
  launchTurnitinAssignment,
  launchTurnitinSubmission,
  fetchAIGradesForStudent,
  hasFetchedStudents,
  fetchParticipantsInSpace,
}) => {
  const routeHistory = useHistory();

  useEffect(() => {
    if (!assignments.length) {
      fetchAssignments({ spaceId });
    }

    if (assignments.length > 0) {
      fetchGradesForStudent({ studentId });
    }

    if (!ltiProviders.length) {
      fetchLtiProviders();
    }
  }, [spaceId, studentId, assignments.length, ltiProviders.length]);

  useEffect(() => {
    if (!assessmentItems.length) {
      fetchAssessmentItems({ spaceId });
    }

    if (assessmentItems.length > 0) {
      fetchAIGradesForStudent({
        studentId,
        assessmentItemIds: assessmentItems.map(({ id }) => id),
      });
    }
  }, [spaceId, studentId, assessmentItems.length]);

  // Student doesn't need to fetch particpants as they can only view themself
  useEffect(() => {
    if (viewAs !== STUDENT && !hasFetchedStudents) {
      fetchParticipantsInSpace();
    }
  }, [spaceId, studentId, hasFetchedStudents]);

  if (isLoading) {
    return (
      <PageContainer hasSideBar={false}>
        <Spinner />
      </PageContainer>
    );
  }

  // for both educator and student:
  // if studentId is incorrect, grades endpoint will throw error
  // for educator:
  // student might be undefined if student is removed from space
  if (error || !student) {
    return <Error hasSideBar={false} />;
  }

  if (assignments.length > 0 && window.location.search) {
    const params = new URLSearchParams(window.location.search);
    if (params.has('launch')) {
      const assignment = assignments.find(
        (assig) => assig.id === params.get('launch')
      );
      if (assignment) {
        return (
          <AutoLaunchExternalLink
            assignment={assignment}
            isEducator={viewAs === EDUCATOR}
            launchTurnitinAssignment={launchTurnitinAssignment}
            launchTurnitinSubmission={launchTurnitinSubmission}
          />
        );
      } else {
        console.warn('Launch parameter found but assignment not found');
      }
    }
  }

  // student can't redirect to assignment page
  const handleAssignmentClick =
    viewAs === STUDENT
      ? undefined
      : (id) => {
          routeHistory.push(`/${spaceId}/assignment/${id}`);
        };

  return (
    <>
      {viewAs !== STUDENT && (
        <ContextBar
          prevLocation={`/${spaceId}/students`}
          prevPageTitle="All students"
        />
      )}
      <PageContainer key="content" hasSideBar={false} testId="student-page">
        <MediumUp>
          <StudentDetailsWrapper>
            <StudentDetails student={student} viewAs={viewAs} />
          </StudentDetailsWrapper>
        </MediumUp>
        <MediumUp>
          <AssessmentItemsView
            assessmentItems={assessmentItems}
            assignments={assignments}
            viewAs={viewAs}
            spaceId={spaceId}
          />
        </MediumUp>
        <Assignments
          assignments={assignments}
          assignmentsLoading={isLoading}
          spaceId={spaceId}
          currentSpace={currentSpace}
          launchTurnitinAssignment={launchTurnitinAssignment}
          launchTurnitinSubmission={launchTurnitinSubmission}
          viewAs={viewAs}
          onClick={handleAssignmentClick}
          isManualAssignmentsEnabled={false}
          ltiProviders={ltiProviders}
        />
      </PageContainer>
    </>
  );
};

StudentPage.propTypes = {
  studentId: PropTypes.string.isRequired,
  spaceId: PropTypes.string.isRequired,
  currentSpace: TYPES.Space,
  viewAs: TYPES.ViewAs,
  error: ErrorType,
  isLoading: PropTypes.bool,
  student: User,
  assessmentItems: PropTypes.arrayOf(AssessmentItemType).isRequired,
  fetchAssessmentItems: PropTypes.func.isRequired,
  assignments: PropTypes.arrayOf(AssignmentType),
  fetchAssignments: PropTypes.func.isRequired,
  fetchGradesForStudent: PropTypes.func.isRequired,
  launchTurnitinAssignment: PropTypes.func.isRequired,
  launchTurnitinSubmission: PropTypes.func.isRequired,
  fetchAIGradesForStudent: PropTypes.func.isRequired,
  hasFetchedStudents: PropTypes.bool.isRequired,
  fetchParticipantsInSpace: PropTypes.func.isRequired,
  fetchLtiProviders: PropTypes.func.isRequired,
  ltiProviders: PropTypes.arrayOf(TYPES.LtiProvider),
};

StudentPage.defaultProps = {
  error: null,
  assignments: [],
  student: undefined,
  viewAs: STUDENT,
  isLoading: true, // to not show error on first render
  ltiProviders: [],
};

export default StudentPage;
